import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Loader2, Upload, Trash2, FileText, ChevronDown, ChevronUp, Camera, X } from "lucide-react"
import { toast } from "sonner"
import { Capacitor } from "@capacitor/core"
import { pickImage } from "@/lib/camera"

import { useCreateTenant, useUpdateTenant } from "@/features/tenants/hooks/useTenants"
import { tenantsService } from "@/features/tenants/services/tenantsService"
import { CURRENCIES } from "@/lib/constants"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Tenant } from "@rentbase/shared"

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const DOCUMENT_CATEGORIES = [
  { value: "id_document", label: "ID Document" },
  { value: "right_to_rent", label: "Right to Rent" },
  { value: "employment_proof", label: "Employment Proof" },
  { value: "reference", label: "Reference" },
  { value: "other", label: "Other" },
]

interface StagedDoc {
  file: File
  category: string
}

const schema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.string().email("Enter a valid email").optional().or(z.literal("")),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  employment_status: z.string().optional(),
  monthly_income: z.coerce.number().optional(),
  income_currency: z.string().optional(),
  emergency_contact_name: z.string().optional(),
  emergency_contact_phone: z.string().optional(),
  notes: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

interface TenantFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  tenant?: Tenant
}

export function TenantForm({ open, onOpenChange, onSuccess, tenant }: TenantFormProps) {
  const isEdit = !!tenant
  const createTenant = useCreateTenant()
  const updateTenant = useUpdateTenant()

  const isPending = createTenant.isPending || updateTenant.isPending

  const [stagedDocs, setStagedDocs] = useState<StagedDoc[]>([])
  const [docsOpen, setDocsOpen] = useState(false)
  const [uploadingDocs, setUploadingDocs] = useState(false)
  const [pendingCategory, setPendingCategory] = useState("other")
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Photo state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [stagedPhoto, setStagedPhoto] = useState<File | null>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      nationality: "",
      employment_status: "",
      income_currency: "EUR",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      notes: "",
    },
  })

  useEffect(() => {
    const t = tenant as unknown as Record<string, unknown>
    if (tenant) {
      form.reset({
        full_name: tenant.full_name ?? "",
        email: tenant.email ?? "",
        phone: (t.phone as string) ?? "",
        nationality: (t.nationality as string) ?? "",
        employment_status: (t.employment_status as string) ?? "",
        monthly_income: (t.monthly_income as number) ?? undefined,
        income_currency: (t.income_currency as string) ?? "EUR",
        emergency_contact_name: (t.emergency_contact_name as string) ?? "",
        emergency_contact_phone: (t.emergency_contact_phone as string) ?? "",
        notes: (t.notes as string) ?? "",
      })
    } else {
      form.reset({
        full_name: "",
        email: "",
        phone: "",
        nationality: "",
        employment_status: "",
        income_currency: "EUR",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        notes: "",
      })
      setStagedDocs([])
    }
    // Reset photo state on open/close
    setStagedPhoto(null)
    setPhotoPreview(null)
  }, [tenant, open, form])

  function handlePhotoFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setStagedPhoto(file)
    setPhotoPreview(URL.createObjectURL(file))
    e.target.value = ""
  }

  async function handlePhotoButtonClick() {
    if (Capacitor.isNativePlatform()) {
      const file = await pickImage()
      if (file) {
        setStagedPhoto(file)
        setPhotoPreview(URL.createObjectURL(file))
      }
    } else {
      photoInputRef.current?.click()
    }
  }

  function clearStagedPhoto() {
    setStagedPhoto(null)
    setPhotoPreview(null)
  }

  async function uploadStagedPhoto(tenantId: string) {
    if (!stagedPhoto) return
    setUploadingPhoto(true)
    try {
      await tenantsService.uploadPhoto(tenantId, stagedPhoto)
    } catch {
      toast.error("Failed to upload photo")
    }
    setStagedPhoto(null)
    setUploadingPhoto(false)
  }

  function handleDocFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setStagedDocs((prev) => [...prev, { file, category: pendingCategory }])
    e.target.value = ""
  }

  function removeStagedDoc(idx: number) {
    setStagedDocs((prev) => prev.filter((_, i) => i !== idx))
  }

  async function uploadStagedDocs(tenantId: string) {
    if (stagedDocs.length === 0) return
    setUploadingDocs(true)
    for (const doc of stagedDocs) {
      try {
        await tenantsService.uploadDocument(tenantId, doc.file, doc.category)
      } catch {
        toast.error(`Failed to upload ${doc.file.name}`)
      }
    }
    setStagedDocs([])
    setUploadingDocs(false)
  }

  function onSubmit(values: FormValues) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== "" && v !== undefined && v !== null)
    )

    if (isEdit && tenant) {
      updateTenant.mutate(
        { id: tenant.id, input: payload },
        {
          onSuccess: async () => {
            await uploadStagedPhoto(tenant.id)
            await uploadStagedDocs(tenant.id)
            toast.success("Tenant updated")
            onSuccess()
            onOpenChange(false)
          },
          onError: (err: unknown) => {
            toast.error(err instanceof Error ? err.message : "Failed to update tenant")
          },
        }
      )
    } else {
      createTenant.mutate(payload, {
        onSuccess: async (newTenant) => {
          const newId = (newTenant as unknown as { id: string }).id
          await uploadStagedPhoto(newId)
          await uploadStagedDocs(newId)
          toast.success("Tenant added")
          onSuccess()
          onOpenChange(false)
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : "Failed to add tenant")
        },
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[92dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>{isEdit ? "Edit Tenant" : "Add Tenant"}</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            {/* Photo uploader */}
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={handlePhotoButtonClick}
                  className="w-20 h-20 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border-2 border-dashed border-primary/30 hover:border-primary/60 transition-colors"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                  ) : (tenant as unknown as { photo_url?: string })?.photo_url ? (
                    <img
                      src={(tenant as unknown as { photo_url?: string }).photo_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl font-bold text-primary">
                      {form.watch("full_name")
                        ? getInitials(form.watch("full_name"))
                        : <Camera className="h-6 w-6 text-primary/50" />}
                    </span>
                  )}
                </button>
                {(photoPreview || stagedPhoto) && (
                  <button
                    type="button"
                    onClick={clearStagedPhoto}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {photoPreview ? "Photo selected" : "Tap to add photo"}
              </span>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoFilePick}
              />
            </div>

            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+44 7700 900000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nationality"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nationality</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. British" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="employment_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employment Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="self-employed">Self-employed</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="monthly_income"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Income</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="income_currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="emergency_contact_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emergency Contact Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Contact name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emergency_contact_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emergency Contact Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+44 7700 900001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional notes..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Documents section */}
            <div className="border border-border rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setDocsOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
              >
                <span>Documents {stagedDocs.length > 0 && `(${stagedDocs.length})`}</span>
                {docsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>

              {docsOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-border">
                  {stagedDocs.length > 0 && (
                    <div className="space-y-2 pt-3">
                      {stagedDocs.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="flex-1 truncate">{doc.file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {DOCUMENT_CATEGORIES.find((c) => c.value === doc.category)?.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeStagedDoc(idx)}
                            className="text-destructive hover:text-destructive/80 shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Select value={pendingCategory} onValueChange={setPendingCategory}>
                      <SelectTrigger className="flex-1 h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Add File
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleDocFilePick}
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl"
              disabled={isPending || uploadingDocs || uploadingPhoto}
            >
              {isPending || uploadingDocs || uploadingPhoto ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uploadingPhoto ? "Uploading photo..." : uploadingDocs ? "Uploading documents..." : "Saving..."}
                </>
              ) : (
                "Save Tenant"
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
